import React, { useRef, useState } from 'react';
import { X, Upload, Image, Film, Plus } from 'lucide-react';
import { COLORS } from '../../utils/constants';

const C = COLORS;
const iStyle = { width: '100%', padding: '10px 14px', border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, outline: 'none', background: C.g50, boxSizing: 'border-box', fontFamily: 'inherit' };

// ─── MODAL ───
export const Modal = ({ title, children, onClose, wide }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={onClose}>
    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, padding: 28, width: '100%', maxWidth: wide ? 680 : 560, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.15)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.g900 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: C.g400 }}><X size={20} /></button>
      </div>
      {children}
    </div>
  </div>
);

// ─── FORM FIELD ───
export const FormField = ({ label, name, type = 'text', value, onChange, placeholder, options, error, required, rows }) => (
  <div style={{ marginBottom: 20 }}>
    <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.g700, letterSpacing: 0.2 }}>
      {label} {required && <span style={{ color: C.danger }}>*</span>}
    </label>
    {options ? (
      <select name={name} value={value || ''} onChange={onChange} style={iStyle}>
        <option value="">{placeholder || 'Select...'}</option>
        {options.map(o => {
          const val = typeof o === 'object' ? o.value : o;
          const lbl = typeof o === 'object' ? o.label : o.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          return <option key={val} value={val}>{lbl}</option>;
        })}
      </select>
    ) : rows ? (
      <textarea name={name} value={value || ''} onChange={onChange} placeholder={placeholder} rows={rows}
        style={{ ...iStyle, resize: 'vertical', minHeight: 100 }} />
    ) : (
      <input type={type} name={name} value={value || ''} onChange={onChange} placeholder={placeholder} style={iStyle} />
    )}
    {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: C.danger }}>{error}</p>}
  </div>
);

// ─── MULTI SELECT (many-to-many picker: chips + checkbox list) ───
export const MultiSelect = ({ label, options = [], value = [], onChange, placeholder, emptyText }) => {
  const selected = (value || []).map(Number);
  const toggle = (val) => {
    const v = Number(val);
    if (selected.includes(v)) onChange(selected.filter(x => x !== v));
    else onChange([...selected, v]);
  };
  const selectedOptions = options.filter(o => selected.includes(Number(o.value)));

  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.g700, letterSpacing: 0.2 }}>{label}</label>

      {selectedOptions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {selectedOptions.map(o => (
            <span key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.primaryGhost, border: `1px solid ${C.primaryLight}`, borderRadius: 10, padding: '5px 10px', fontSize: 12, color: C.primaryDark, fontWeight: 600 }}>
              {o.label}
              <button type="button" onClick={() => toggle(o.value)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.primary, padding: 0, display: 'flex' }}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}

      <div style={{ border: `1px solid ${C.g200}`, borderRadius: 10, maxHeight: 180, overflowY: 'auto', background: C.g50 }}>
        {options.length === 0 ? (
          <div style={{ padding: '12px 14px', fontSize: 13, color: C.g400 }}>{emptyText || placeholder || 'No options available'}</div>
        ) : options.map(o => {
          const checked = selected.includes(Number(o.value));
          return (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer', fontSize: 14, color: C.g700, borderBottom: `1px solid ${C.g100}`, background: checked ? C.primaryGhost : 'transparent' }}>
              <input type="checkbox" checked={checked} onChange={() => toggle(o.value)} style={{ accentColor: C.primary, width: 16, height: 16, cursor: 'pointer' }} />
              <span>{o.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

// ─── FILE UPLOAD (single or multi) ───
export const FileUpload = ({ label, name, multiple, files, onChange, onRemove }) => {
  const ref = useRef();
  const handleClick = () => ref.current?.click();
  const handleChange = (e) => onChange(e.target.files);

  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.g700 }}>{label}</label>
      <input ref={ref} type="file" name={name} multiple={multiple} accept="image/*,video/*" style={{ display: 'none' }} onChange={handleChange} />
      <div onClick={handleClick} style={{ border: `2px dashed ${C.g300}`, borderRadius: 12, padding: files?.length ? 16 : 30, textAlign: 'center', cursor: 'pointer', background: C.g50 }}>
        {!files?.length ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
              <Image size={22} style={{ color: C.g400 }} />
              {multiple && <Film size={22} style={{ color: C.g400 }} />}
            </div>
            <p style={{ margin: 0, fontSize: 13, color: C.g500 }}>{multiple ? 'Click to upload images & videos' : 'Click to upload'}</p>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: C.g400 }}>{multiple ? 'PNG, JPG, MP4 — multiple files' : 'PNG, JPG up to 5MB'}</p>
          </>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-start' }}>
            {files.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 10, padding: '6px 10px', fontSize: 12 }}>
                {f.type?.startsWith('video') ? <Film size={14} style={{ color: C.primary }} /> : <Image size={14} style={{ color: C.success }} />}
                <span style={{ color: C.g700, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                <button onClick={e => { e.stopPropagation(); onRemove?.(i); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 2 }}><X size={12} /></button>
              </div>
            ))}
            {multiple && <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.primary, fontSize: 12, fontWeight: 600, padding: '6px 10px', border: `1px dashed ${C.primary}`, borderRadius: 10, background: C.primaryGhost }}><Plus size={14} /> Add more</div>}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── PRIMARY BUTTON ───
export const PrimaryButton = ({ children, onClick, icon, style, disabled, type = 'button' }) => (
  <button type={type} onClick={onClick} disabled={disabled}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 22px', background: disabled ? C.g300 : C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', boxShadow: disabled ? 'none' : '0 2px 8px rgba(79,70,229,0.25)', transition: 'all 0.15s', ...style }}>
    {icon}{children}
  </button>
);

// ─── SECONDARY BUTTON ───
export const SecondaryButton = ({ children, onClick, style }) => (
  <button onClick={onClick}
    style={{ padding: '10px 20px', border: `1px solid ${C.g200}`, borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit', color: C.g600, ...style }}>
    {children}
  </button>
);
